// src/pages/admin/AdminLabsPage.jsx — Pro v2 uniforme
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  Stack, Snackbar, Alert, Chip, Tooltip, Select, MenuItem,
  InputAdornment, LinearProgress, Divider, Pagination,
  CircularProgress, Paper, FormControl, InputLabel,
} from "@mui/material";
import AddIcon              from "@mui/icons-material/Add";
import EditIcon             from "@mui/icons-material/Edit";
import DeleteIcon           from "@mui/icons-material/Delete";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ContentCopyIcon      from "@mui/icons-material/ContentCopy";
import UploadFileIcon       from "@mui/icons-material/UploadFile";
import CodeIcon             from "@mui/icons-material/Code";
import SearchIcon           from "@mui/icons-material/Search";
import RefreshIcon          from "@mui/icons-material/Refresh";
import ScienceIcon          from "@mui/icons-material/Science";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import api, { getUserMessage } from "../../api/axios";
import { pageWrap, tableHeaderCell, tableRow, btnPrimary, C } from "../../utils/pageStyles";

const r2 = { borderRadius: 2 };

export default function AdminLabsPage() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [allLabs,    setAllLabs]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [reload,     setReload]     = useState(0);
  const [page,       setPage]       = useState(1);
  const PAGE = 15;

  const [search,       setSearch]       = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [topoFilter,   setTopoFilter]   = useState("all");
  const [quotaFilter,  setQuotaFilter]  = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const EMPTY = {
    name:"", description:"", topology_file:"", template_project_id:"",
    capacity:"", max_daily_minutes_per_user:"", max_weekly_minutes_per_user:"",
    max_active_reservations_per_user:"", is_active:true,
  };
  const [form, setForm] = useState(EMPTY);

  const [jsonOpen,    setJsonOpen]    = useState(false);
  const [jsonLab,     setJsonLab]     = useState(null);
  const [jsonData,    setJsonData]    = useState(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [labForUpload, setLabForUpload] = useState(null);

  const [snack, setSnack] = useState(null);
  const notify = (text, sev="success") => setSnack({ text, sev });

  // ── Data ──────────────────────────────────────────────────────────────────
  const fetchLabs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/labs", { params:{ page:1, page_size:1000 } });
      const d = res.data;
      setAllLabs(Array.isArray(d) ? d : (d?.items || []));
    } catch(e) { notify(getUserMessage(e)||t("errors.load_labs_admin"), "error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchLabs(); }, [reload]);

  const hasQuota = (l) =>
    (l.max_daily_minutes_per_user||0)>0 ||
    (l.max_weekly_minutes_per_user||0)>0 ||
    (l.max_active_reservations_per_user||0)>0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allLabs.filter(l => {
      if (activeFilter==="active"   && !l.is_active) return false;
      if (activeFilter==="inactive" &&  l.is_active) return false;
      const topo = l.topology_file?.trim();
      if (topoFilter==="with"    && !topo) return false;
      if (topoFilter==="without" &&  topo) return false;
      if (quotaFilter==="with"   && !hasQuota(l)) return false;
      if (q && !["name","description","topology_file","template_project_id"]
        .some(f => (l[f]||"").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [allLabs, search, activeFilter, topoFilter, quotaFilter]);

  const paginated  = filtered.slice((page-1)*PAGE, page*PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length/PAGE));

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openCreate = () => { setEditingLab(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit   = (l) => {
    setEditingLab(l);
    const n = v => (typeof v==="number"&&!isNaN(v)) ? String(v) : "";
    setForm({ name:l.name||"", description:l.description||"",
      topology_file:l.topology_file||"", template_project_id:l.template_project_id||"",
      capacity:n(l.capacity), max_daily_minutes_per_user:n(l.max_daily_minutes_per_user),
      max_weekly_minutes_per_user:n(l.max_weekly_minutes_per_user),
      max_active_reservations_per_user:n(l.max_active_reservations_per_user),
      is_active:!!l.is_active });
    setDialogOpen(true);
  };
  const setF = k => e => setForm(p => ({ ...p, [k]: k==="is_active" ? e.target.checked : e.target.value }));
  const num  = v => v!==""&&v!=null ? Number(v) : null;

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { notify(t("labs.name")+" requis","warning"); return; }
    setSaving(true);
    try {
      const p = { name:form.name.trim(), description:form.description?.trim()||null,
        topology_file:form.topology_file?.trim()||null,
        template_project_id:form.template_project_id?.trim()||null,
        is_active:!!form.is_active, capacity:num(form.capacity),
        max_daily_minutes_per_user:num(form.max_daily_minutes_per_user),
        max_weekly_minutes_per_user:num(form.max_weekly_minutes_per_user),
        max_active_reservations_per_user:num(form.max_active_reservations_per_user),
      };
      if (editingLab) { await api.put(`/admin/labs/${editingLab.id}`,p); notify(t("admin.lab_updated")); }
      else             { await api.post("/admin/labs",p);                 notify(t("admin.lab_created")); }
      setDialogOpen(false); setReload(c=>c+1);
    } catch(e) { notify(getUserMessage(e)||t("errors.save_lab"),"error"); }
    finally { setSaving(false); }
  };
  const handleDelete = async id => {
    if (!window.confirm(t("admin.confirm_delete_lab"))) return;
    try { await api.delete(`/admin/labs/${id}`); notify(t("admin.lab_deleted")); setReload(c=>c+1); }
    catch(e) { notify(getUserMessage(e)||t("errors.delete_lab"),"error"); }
  };
  const handleToggle = async l => {
    try { const r=await api.patch(`/admin/labs/${l.id}/toggle-active`);
      notify(r.data.is_active?t("admin.lab_activated"):t("admin.lab_deactivated")); setReload(c=>c+1); }
    catch(e) { notify(getUserMessage(e),"error"); }
  };
  const handleDuplicate = async l => {
    try { const r=await api.post(`/admin/labs/${l.id}/duplicate`);
      notify(`Dupliqué : ${r?.data?.name||"copie"}`); setReload(c=>c+1); }
    catch(e) { notify(getUserMessage(e)||t("errors.duplicate_lab"),"error"); }
  };
  const handleUploadClick = l => {
    setLabForUpload(l); fileInputRef.current.value=""; fileInputRef.current.click();
  };
  const handleFileChange = async e => {
    const f=e.target.files?.[0]; if(!f||!labForUpload){setLabForUpload(null);return;}
    try { const fd=new FormData(); fd.append("file",f);
      await api.post(`/admin/labs/${labForUpload.id}/upload-topology`,fd,
        {headers:{"Content-Type":"multipart/form-data"}});
      notify(t("admin.topology_sent")); setReload(c=>c+1); }
    catch(e) { notify(getUserMessage(e)||t("errors.upload_topology"),"error"); }
    finally { setLabForUpload(null); }
  };
  const handleJsonView = async l => {
    setJsonLab(l); setJsonOpen(true); setJsonData(null); setJsonLoading(true);
    try { const r=await api.get(`/admin/labs/${l.id}/topology-json`); setJsonData(r.data); }
    catch(e) { notify(getUserMessage(e),"error"); }
    finally { setJsonLoading(false); }
  };

  const tfSx = { "& .MuiOutlinedInput-root": { ...r2 } };

  return (
    <Box sx={pageWrap}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between"
        alignItems={{ sm:"center" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScienceIcon sx={{ fontSize: 22, color: "#6366f1" }} />
            <Typography variant="h5" fontWeight={700}>{t("admin.labs_title")}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {filtered.length} lab{filtered.length!==1?"s":""} · {allLabs.filter(l=>l.is_active).length} {t("admin.labs_active").toLowerCase()}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Tooltip title={t("common.refresh")}>
            <IconButton onClick={() => setReload(c=>c+1)} disabled={loading} size="small"
              sx={{ border:"1px solid", borderColor:"divider", ...r2 }}>
              {loading ? <CircularProgress size={16}/> : <RefreshIcon fontSize="small"/>}
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon/>} onClick={openCreate}
            sx={{ ...r2, ...btnPrimary }}>
            {t("admin.create_lab")}
          </Button>
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Filtres ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider",
        borderRadius: 3, p: 2, mb: 2.5 }}>
        <Stack direction={{ xs:"column", sm:"row" }} spacing={1.5} alignItems="center" flexWrap="wrap">
          <TextField size="small" placeholder={t("admin.search_lab")} value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} sx={{ ...tfSx, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start">
              <SearchIcon sx={{ fontSize:16, color:"text.disabled" }}/></InputAdornment> }} />

          {[
            { val:activeFilter, set:v=>{setActiveFilter(v);setPage(1);},
              opts:[[" all",t("admin.labs_all")],["active",t("admin.labs_active")],["inactive",t("admin.labs_inactive")]] },
            { val:topoFilter, set:v=>{setTopoFilter(v);setPage(1);},
              opts:[["all","Topologie : Toutes"],["with",t("admin.labs_with_topology")],["without",t("admin.labs_without_topology")]] },
            { val:quotaFilter, set:v=>{setQuotaFilter(v);setPage(1);},
              opts:[["all","Quotas : Tous"],["with",t("admin.labs_with_quotas")]] },
          ].map((f,i) => (
            <FormControl key={i} size="small" sx={{ minWidth: 170 }}>
              <Select value={f.val} onChange={e=>f.set(e.target.value)} sx={{ ...r2 }}>
                {f.opts.map(([v,l]) => <MenuItem key={v} value={v.trim()}>{l}</MenuItem>)}
              </Select>
            </FormControl>
          ))}
        </Stack>
      </Paper>

      {/* ── Tableau ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border:"1px solid", borderColor:"divider", borderRadius: 3, overflow:"hidden" }}>
        <Box component="table" sx={{ width:"100%", borderCollapse:"collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor:"#f8fafc" }}>
              {[t("labs.name"), t("admin.col_description"), t("admin.col_template_project"),
                t("admin.col_topology_file"), t("admin.col_capacity"), t("admin.col_quotas"),
                t("admin.col_active"), t("common.actions")].map((h,i) => (
                <Box component="th" key={i}
                  sx={{ ...tableHeaderCell, p:1.5, textAlign:i===7?"right":"left" }}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {paginated.length===0 && (
              <Box component="tr">
                <Box component="td" colSpan={8}
                  sx={{ py:6, textAlign:"center", color:"text.secondary" }}>
                  {loading ? t("common.loading") : t("admin.no_lab_found")}
                </Box>
              </Box>
            )}
            {paginated.map(lab => (
              <Box component="tr" key={lab.id}
                sx={{ ...tableRow, "& td":{ p:1.5, borderBottom:"1px solid #f1f5f9" } }}>

                {/* Nom */}
                <Box component="td">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <FiberManualRecordIcon sx={{ fontSize:9,
                      color: lab.is_active ? "#22c55e" : "#cbd5e1" }} />
                    <Typography variant="body2" fontWeight={600}>{lab.name}</Typography>
                  </Stack>
                </Box>

                {/* Description */}
                <Box component="td">
                  <Typography variant="body2" color="text.secondary"
                    sx={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {lab.description || "—"}
                  </Typography>
                </Box>

                {/* Template */}
                <Box component="td">
                  {lab.template_project_id
                    ? <Tooltip title={lab.template_project_id}>
                        <Typography variant="caption"
                          sx={{ fontFamily:"monospace", color:"#6366f1", cursor:"default" }}>
                          {lab.template_project_id.substring(0,8)}…
                        </Typography>
                      </Tooltip>
                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                </Box>

                {/* Topologie */}
                <Box component="td">
                  {lab.topology_file
                    ? <Chip size="small" label={lab.topology_file.split("/").pop().substring(0,18)}
                        sx={{ height:18, fontSize:10, bgcolor:"#eef2ff", color:"#6366f1",
                          fontFamily:"monospace" }}/>
                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                </Box>

                {/* Capacité */}
                <Box component="td">
                  <Typography variant="body2" fontWeight={600}>
                    {lab.capacity ?? <span style={{color:"#cbd5e1"}}>—</span>}
                  </Typography>
                </Box>

                {/* Quotas */}
                <Box component="td">
                  {hasQuota(lab) ? (
                    <Stack spacing={0.25}>
                      {lab.max_daily_minutes_per_user>0 &&
                        <Typography sx={{fontSize:10,color:"#f59e0b",lineHeight:1.3}}>
                          {lab.max_daily_minutes_per_user} min/j</Typography>}
                      {lab.max_weekly_minutes_per_user>0 &&
                        <Typography sx={{fontSize:10,color:"#6366f1",lineHeight:1.3}}>
                          {lab.max_weekly_minutes_per_user} min/sem</Typography>}
                      {lab.max_active_reservations_per_user>0 &&
                        <Typography sx={{fontSize:10,color:"#64748b",lineHeight:1.3}}>
                          max {lab.max_active_reservations_per_user}</Typography>}
                    </Stack>
                  ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                </Box>

                {/* Actif */}
                <Box component="td">
                  {lab.is_active
                    ? <Chip size="small" label="Actif"
                        sx={{ height:18, fontSize:10, fontWeight:700, bgcolor:"#f0fdf4", color:"#16a34a" }}/>
                    : <Chip size="small" label="Inactif"
                        sx={{ height:18, fontSize:10, fontWeight:700, bgcolor:"#f8fafc", color:"#94a3b8" }}/>}
                </Box>

                {/* Actions */}
                <Box component="td" sx={{ textAlign:"right" }}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {[
                      { icon:<PowerSettingsNewIcon sx={{fontSize:16}}/>,
                        tip: lab.is_active?t("admin.deactivate_lab"):"Activer",
                        fn: ()=>handleToggle(lab),
                        sx:{ color: lab.is_active?"#f59e0b":"#22c55e",
                          "&:hover":{ bgcolor: lab.is_active?"#fffbeb":"#f0fdf4" }}},
                      { icon:<ContentCopyIcon sx={{fontSize:16}}/>, tip:t("common.duplicate"),
                        fn:()=>handleDuplicate(lab),
                        sx:{ color:"#6366f1","&:hover":{ bgcolor:"#eef2ff" }}},
                      { icon:<UploadFileIcon sx={{fontSize:16}}/>, tip:"Upload topologie",
                        fn:()=>handleUploadClick(lab),
                        sx:{ color:"#64748b","&:hover":{ bgcolor:"#f8fafc" }}},
                      { icon:<CodeIcon sx={{fontSize:16}}/>, tip:"Voir JSON",
                        fn:()=>handleJsonView(lab), disabled:!lab.topology_file,
                        sx:{ color:"#64748b","&:hover":{ bgcolor:"#f8fafc" }}},
                      { icon:<EditIcon sx={{fontSize:16}}/>, tip:t("common.edit"),
                        fn:()=>openEdit(lab),
                        sx:{ color:"#64748b","&:hover":{ bgcolor:"#f8fafc" }}},
                      { icon:<DeleteIcon sx={{fontSize:16}}/>, tip:t("common.delete"),
                        fn:()=>handleDelete(lab.id),
                        sx:{ color:"#ef4444","&:hover":{ bgcolor:"#fef2f2" }}},
                    ].map((a,i) => (
                      <Tooltip key={i} title={a.tip}><span>
                        <IconButton size="small" onClick={a.fn} disabled={a.disabled}
                          sx={{ borderRadius: 1.5, "& .Mui-disabled":{ opacity:.3 }, ...a.sx }}>
                          {a.icon}
                        </IconButton>
                      </span></Tooltip>
                    ))}
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Stack direction="row" justifyContent="space-between" alignItems="center"
            sx={{ px:3, py:1.5, borderTop:"1px solid", borderColor:"divider" }}>
            <Typography variant="caption" color="text.secondary">
              {filtered.length} résultat{filtered.length!==1?"s":""} · page {page}/{totalPages}
            </Typography>
            <Pagination count={totalPages} page={page} size="small"
              onChange={(_,v) => setPage(v)}
              sx={{ "& .MuiPaginationItem-root":{ borderRadius:1.5 } }}/>
          </Stack>
        )}
      </Paper>

      <input ref={fileInputRef} type="file" style={{display:"none"}}
        accept=".zip,.gns3project" onChange={handleFileChange}/>

      {/* ── Dialog créer / éditer ──────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={()=>setDialogOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx:{ borderRadius:3 } }}>
        <DialogTitle sx={{ fontWeight:700, borderBottom:"1px solid", borderColor:"divider", pb:2 }}>
          {editingLab ? t("common.edit")+" — "+editingLab.name : t("admin.create_lab")}
        </DialogTitle>
        <DialogContent sx={{ pt:3 }}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs:"column", md:"row" }} spacing={2}>
              <TextField label={t("labs.name")} size="small" fullWidth required
                value={form.name} onChange={setF("name")} sx={tfSx}/>
              <TextField label={t("admin.col_template_project")} size="small" fullWidth
                value={form.template_project_id} onChange={setF("template_project_id")}
                placeholder="UUID GNS3" sx={tfSx}/>
            </Stack>
            <TextField label={t("admin.col_description")} size="small" fullWidth multiline minRows={2}
              value={form.description} onChange={setF("description")} sx={tfSx}/>
            <TextField label={t("admin.col_topology_file")} size="small" fullWidth
              value={form.topology_file} onChange={setF("topology_file")}
              placeholder="chemin/fichier.gns3project" sx={tfSx}/>

            <Divider />
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform:"uppercase", letterSpacing:".6px" }}>
              Quotas & capacité
            </Typography>

            <Stack direction={{ xs:"column", md:"row" }} spacing={2} flexWrap="wrap">
              {[
                { k:"capacity",                       label:t("admin.capacity_label") },
                { k:"max_daily_minutes_per_user",     label:t("adminLabRules.columns.dailyMinutesPerUser") },
                { k:"max_weekly_minutes_per_user",    label:t("adminLabRules.columns.weeklyMinutesPerUser") },
                { k:"max_active_reservations_per_user", label:t("admin.max_reservations_per_user_label") },
              ].map(({k,label}) => (
                <TextField key={k} label={label} size="small" type="number"
                  sx={{ ...tfSx, flex:"0 0 calc(50% - 8px)" }}
                  value={form[k]} onChange={setF(k)} inputProps={{ min:0 }}/>
              ))}
            </Stack>

            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={setF("is_active")}/>}
              label={<Typography variant="body2">{form.is_active?"Lab actif":"Lab inactif"}</Typography>}/>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop:"1px solid", borderColor:"divider", px:3, py:2, gap:1 }}>
          <Button onClick={()=>setDialogOpen(false)} sx={r2}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving?<CircularProgress size={14} color="inherit"/>:null}
            sx={{ ...r2, ...btnPrimary }}>
            {saving?"Enregistrement…":t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog JSON viewer ─────────────────────────────────────────── */}
      <Dialog open={jsonOpen} onClose={()=>setJsonOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx:{ borderRadius:3 } }}>
        <DialogTitle sx={{ fontWeight:700, borderBottom:"1px solid", borderColor:"divider" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CodeIcon sx={{ color:"#6366f1", fontSize:18 }}/>
            <span>Topologie — {jsonLab?.name}</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p:0 }}>
          {jsonLoading && <Box sx={{ p:4, textAlign:"center" }}><CircularProgress/></Box>}
          {!jsonLoading && jsonData && (
            <>
              <Box sx={{ px:3, py:2, borderBottom:"1px solid", borderColor:"divider", bgcolor:"#f8fafc" }}>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {[
                    { l:"Projet", v:jsonData.summary?.project_id?.substring(0,12)+"…" },
                    { l:"Nœuds",  v:jsonData.summary?.nodes_count },
                    { l:"Liens",  v:jsonData.summary?.links_count },
                  ].map(({l,v}) => (
                    <Box key={l} sx={{ bgcolor:"#fff", border:"1px solid", borderColor:"divider",
                      borderRadius:2, px:2, py:1 }}>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform:"uppercase", letterSpacing:".5px" }}>{l}</Typography>
                      <Typography variant="body2" fontWeight={700}
                        sx={{ fontFamily:"monospace", color:"#6366f1" }}>{v??""}</Typography>
                    </Box>
                  ))}
                </Stack>
                {jsonData.summary?.node_names?.length>0 && (
                  <Stack direction="row" spacing={0.75} sx={{ mt:1.5 }} flexWrap="wrap">
                    {jsonData.summary.node_names.map(n => (
                      <Chip key={n} size="small" label={n}
                        sx={{ height:18, fontSize:10, bgcolor:"#eef2ff", color:"#6366f1",
                          fontFamily:"monospace" }}/>
                    ))}
                  </Stack>
                )}
              </Box>
              <Box sx={{ fontFamily:"Monaco,Menlo,monospace", fontSize:11.5, color:"#e6edf3",
                whiteSpace:"pre", overflowX:"auto", maxHeight:420, p:2.5, bgcolor:"#0d1117",
                "&::-webkit-scrollbar":{width:6}, "&::-webkit-scrollbar-thumb":{bgcolor:"#374151",borderRadius:3} }}>
                {JSON.stringify(jsonData.raw, null, 2)}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop:"1px solid", borderColor:"divider", px:3 }}>
          <Button onClick={()=>setJsonOpen(false)} sx={r2}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={!!snack} autoHideDuration={4000}
        onClose={()=>setSnack(null)} anchorOrigin={{vertical:"bottom",horizontal:"center"}}>
        <Alert severity={snack?.sev||"success"} onClose={()=>setSnack(null)}
          sx={{borderRadius:2}}>{snack?.text}</Alert>
      </Snackbar>
    </Box>
  );
}
